import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { columnMinShare, columnsTemplate, resizeBoundary } from '../../lib/columns'

type Side = 'left' | 'right'

interface Dragging {
  pointer: number
  x: number
  from: number[]
  /** The cells' combined width in px (the row less its gaps). */
  usable: number
  grid: HTMLElement
  now: number[]
}

/** Arrow keys move the gutter a step (a share of the row); PageUp/PageDown a bigger one. */
const STEPS: Record<string, number> = { ArrowRight: 0.02, ArrowLeft: -0.02, PageUp: 0.1, PageDown: -0.1 }

/** Two shares as Composer v3 shows them on a held gutter: "60 / 40". */
const pair = (a: number, b: number) => `${Math.round(a * 100)} / ${Math.round(b * 100)}`

/** The round knob on a gutter (Composer v3): a ↔ to drag, or a + to add a column. */
export function GutterKnob({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span
      aria-hidden
      className={`pointer-events-none relative flex size-[26px] items-center justify-center rounded-full border border-foreground/12 bg-card text-foreground/55 shadow-[0_4px_12px_rgba(45,27,30,.08)] transition-opacity ${className}`}
    >
      {children}
    </span>
  )
}

const ARROWS = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 7l-5 5 5 5M16 7l5 5-5 5M3 12h18" />
  </svg>
)

/** Where something dragged will land as a new column (Composer v3): a plum line in the gutter, "↔ column" on it. */
export function ColumnDropLine({ className = '' }: { className?: string }) {
  return (
    <span aria-hidden className={`pointer-events-none absolute w-0.5 rounded-full bg-primary ${className}`}>
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-primary px-2 py-[3px] font-sans text-[10px] font-semibold leading-none text-primary-foreground">
        ↔ column
      </span>
    </span>
  )
}

/**
 * The gutter between two columns in the composer, as in Composer v3: a thin
 * line down the middle of the gap, faint whenever the columns are pointed at
 * (and always a little, so it can be found). Hover it and a ↔ knob shows;
 * drag it sideways and one column grows as the other shrinks (live,
 * committed on release), the line turning plum with the split ("60 / 40")
 * on it. Arrow keys step it; a double click evens the columns out. While a
 * block or thought is dragged over it, it lights up with "↔ column": a new
 * column there. Hidden where the columns stack (index.css .wr-gutter).
 */
export function ColumnGutter({
  boundary,
  widths,
  active,
  dragging,
  onResize,
}: {
  /** The cell it sits before (1 … n−1). */
  boundary: number
  /** Every cell's share of the row now (equal when nothing is stored). */
  widths: number[]
  /** A dragged block is over it: dropping makes a new column here. */
  active: boolean
  /** Something is being dragged over the page: show where the gutters are. */
  dragging: boolean
  onResize: (widths: number[] | null) => void
}) {
  const drag = useRef<Dragging | null>(null)
  // While held: the widths now, for the split shown on the line.
  const [held, setHeld] = useState<number[] | null>(null)
  const min = columnMinShare(widths.length)
  const shown = held ?? widths
  const share = Math.round(shown[boundary - 1] * 100)

  const onDown = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const grid = e.currentTarget.closest<HTMLElement>('.wr-cols-grid')
    if (!grid) return
    const gap = parseFloat(getComputedStyle(grid).columnGap) || 0
    const usable = grid.getBoundingClientRect().width - gap * (widths.length - 1)
    if (!(usable > 0)) return
    e.preventDefault() // no text selection, no focus change
    e.stopPropagation()
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      /* not a live pointer (a synthetic event) */
    }
    drag.current = { pointer: e.pointerId, x: e.clientX, from: widths, usable, grid, now: widths }
    setHeld(widths)
  }
  const onMove = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d || e.pointerId !== d.pointer) return
    d.now = resizeBoundary(d.from, boundary, (e.clientX - d.x) / d.usable, min)
    // Live, straight onto the grid: the document is written once, on release.
    d.grid.style.setProperty('--wr-cols-tpl', columnsTemplate(d.now) ?? '')
    setHeld(d.now)
  }
  const onUp = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d || e.pointerId !== d.pointer) return
    drag.current = null
    setHeld(null)
    if (d.now !== d.from) onResize(d.now)
  }
  const onCancel = () => {
    const d = drag.current
    if (!d) return
    drag.current = null
    setHeld(null)
    d.grid.style.setProperty('--wr-cols-tpl', columnsTemplate(d.from) ?? '')
  }
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return
    const step = STEPS[e.key]
    if (e.key === 'Home' || e.key === 'End' || step !== undefined) {
      e.preventDefault()
      e.stopPropagation()
      const delta = step ?? (e.key === 'Home' ? -1 : 1)
      onResize(resizeBoundary(widths, boundary, delta, min))
    } else if (e.key === 'Enter' || e.key === 'Delete' || e.key === 'Backspace') {
      // Equal again (Backspace and Delete never reach the page from here).
      e.preventDefault()
      e.stopPropagation()
      onResize(null)
    }
  }

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label="Resize columns"
      aria-valuemin={Math.round(min * 100)}
      aria-valuemax={Math.round((widths[boundary - 1] + widths[boundary] - min) * 100)}
      aria-valuenow={share}
      aria-valuetext={`${share}% and ${Math.round(shown[boundary] * 100)}%`}
      title="Drag to resize the columns · double-click to even them out"
      data-gutter={boundary}
      data-active={active ? '' : undefined}
      data-held={held ? '' : undefined}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      onLostPointerCapture={onCancel}
      onKeyDown={onKey}
      onDoubleClick={(e) => {
        e.stopPropagation()
        onResize(null)
      }}
      onClick={(e) => e.stopPropagation()}
      className="wr-gutter group/gutter select-none outline-none"
    >
      {active ? (
        <ColumnDropLine className="inset-y-0 left-1/2 -translate-x-1/2" />
      ) : (
        <>
          {/* The gutter itself: a line the height of the columns. Ink, faint; plum while held. */}
          <span
            aria-hidden
            className={`pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded-full transition-colors ${
              held
                ? 'bg-primary/55'
                : `group-hover/gutter:bg-foreground/22 group-focus-visible/gutter:bg-foreground/22 ${dragging ? 'bg-foreground/14' : 'bg-foreground/[.07] group-hover/cols:bg-foreground/12'}`
            }`}
          />
          {held ? (
            <span
              aria-hidden
              className="pointer-events-none relative whitespace-nowrap rounded-full border border-foreground/12 bg-card px-2 py-[3px] font-sans text-[10px] font-semibold tabular-nums leading-none text-foreground/60"
            >
              {pair(held[boundary - 1], held[boundary])}
            </span>
          ) : (
            // The ↔ knob: shows while the columns are pointed at (so the gutter can be found), full on the gutter itself.
            <GutterKnob className="opacity-0 group-hover/cols:opacity-60 group-hover/gutter:opacity-100 group-focus-visible/gutter:opacity-100">{ARROWS}</GutterKnob>
          )}
        </>
      )}
    </div>
  )
}

/**
 * A wrapped photo floats beside `row`, or is in it: the text there runs
 * round the photo, so a column beside it would only start a new row.
 */
export function besideAWrap(row: Element | null): boolean {
  if (!row) return false
  const r = row.getBoundingClientRect()
  const scope = row.closest('.wr-cell, .wr-column') ?? document
  return Array.from(scope.querySelectorAll<HTMLElement>('figure[data-selectable]')).some((f) => {
    if (getComputedStyle(f).float === 'none') return false
    if (row.contains(f)) return true
    const b = f.getBoundingClientRect()
    return b.top < r.bottom - 1 && b.bottom > r.top + 1
  })
}

/**
 * Just outside a block's left or right edge, where a gutter would be: hover
 * there and a faint gutter line shows with a + knob on it, the same knob as
 * a real gutter's. Click it for a new, empty column on that side (the same
 * as dropping something there). Only where there's a pointer that hovers and
 * room beside the column (index.css .wr-beside).
 */
export function AddColumnBeside({ sides, onAdd }: { sides: Side[]; onAdd: (side: Side) => void }) {
  // Checked as the pointer comes in: a block a wrapped photo sits beside has no room for a column (Niki, 10/5).
  // The left one stands clear of the block's six-dot grip (20px out), so its "+" never covers it.
  const [blocked, setBlocked] = useState(false)
  return (
    <>
      {sides.map((side) => (
        <div
          key={side}
          data-beside={side}
          onPointerEnter={(e) => setBlocked(besideAWrap(e.currentTarget.closest('[data-row]')))}
          className={`wr-beside group/beside ${side === 'left' ? 'right-[calc(100%+20px)]' : 'left-full'} ${blocked ? '[&>*]:invisible' : ''}`}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded-full bg-foreground/22 opacity-0 transition-opacity group-hover/beside:opacity-100"
          />
          <button
            type="button"
            aria-label={`Add a column on the ${side}`}
            title={`Add a column on the ${side}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation()
              if (besideAWrap(e.currentTarget.closest('[data-row]'))) return
              onAdd(side)
            }}
            className="relative rounded-full opacity-0 outline-none transition-opacity focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40 group-hover/beside:opacity-100 [&:hover>span]:text-foreground"
          >
            <GutterKnob>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </GutterKnob>
          </button>
        </div>
      ))}
    </>
  )
}
